import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  Typography,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import CustomTextField from "../../components/common/Textfield";
import CustomSelect from "../../components/common/Select";
import { tokens } from "../../theme";
import { IAddEquipmentDialogProps } from "../../types/equipment";
import apiService from "../../service/Api";

type EquipmentAPIResponse = {
  id: string | number;
  serial_number: string;
  name: string;
  brand: string;
  category: string;
  sub_category: string;
  purchase_date: string;
  status: string;
};

type FormDataType = {
  serialNo: string;
  name: string;
  brand: string;
  category: string;
  subCategory: string;
  purchaseDate: string;
  status: string;
};

const initialFormData: FormDataType = {
  serialNo: "",
  name: "",
  brand: "",
  category: "",
  subCategory: "",
  purchaseDate: "",
  status: "",
};

const AddEquipmentDialog: React.FC<IAddEquipmentDialogProps> = ({
  open,
  onClose,
  onSubmit,
  equipment,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (equipment && isEdit) {
      setFormData({
        serialNo: equipment.serialNo,
        name: equipment.name,
        brand: equipment.brand,
        category: equipment.category,
        subCategory: equipment.subCategory,
        purchaseDate: equipment.purchaseDate,
        status: equipment.status,
      });
    } else {
      setFormData(initialFormData);
    }
    setError(null); // Reset error on open
  }, [equipment, isEdit, open]);

  const handleChange = (field: keyof FormDataType, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields: (keyof FormDataType)[] = [
      "serialNo",
      "name",
      "brand",
      "category",
      "status",
    ];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`Please fill in the ${field} field.`);
        return;
      }
    }
    // Serial number must contain both letters and numbers
    const { serialNo } = formData;
    const hasLetter = /[a-zA-Z]/.test(serialNo);
    const hasNumber = /[0-9]/.test(serialNo);
    if (!hasLetter || !hasNumber) {
      setError("Serial number must contain both letters and numbers.");
      return;
    }
    // Ensure purchaseDate is in YYYY-MM-DD format
    let { purchaseDate } = formData;
    if (purchaseDate) {
      // If the value is not already in YYYY-MM-DD, try to convert it
      const dateObj = new Date(purchaseDate);
      if (!isNaN(dateObj.getTime())) {
        purchaseDate = dateObj.toISOString().slice(0, 10);
      } else {
        setError("Invalid purchase date.");
        return;
      }
    }
    setLoading(true);
    try {
      // Make API call to save equipment to backend
      const payload = {
        serial_number: formData.serialNo,
        name: formData.name,
        brand: formData.brand,
        category: formData.category,
        sub_category: formData.subCategory,
        purchase_date: purchaseDate,
        status: formData.status,
      };
      const response = await apiService.post("/api/equipments/equipment/", payload);
      const data = response.data as EquipmentAPIResponse;
      const created = {
        id: data.id?.toString() || Date.now().toString() + Math.random().toString(36).slice(2),
        serialNo: data.serial_number || formData.serialNo,
        name: data.name || formData.name,
        brand: data.brand || formData.brand,
        category: data.category || formData.category,
        subCategory: data.sub_category || formData.subCategory,
        purchaseDate: data.purchase_date || purchaseDate || "",
        status: data.status || formData.status,
      };
      setError(null);
      onSubmit(created);
      handleDialogClose();
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to create equipment";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setFormData(initialFormData);
    setError(null);
    onClose();
  };

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const brands = ["Sony", "Canon", "Nikon", "Panasonic", "Fujifilm"];
  const categories = ["Camera", "Audio", "Lighting", "Accessories"];
  const subCategories = ["Mirrorless", "DSLR", "Compact", "Action"];
  const statuses = ["Active", "Inactive", "Under Maintenance", "Retired"];

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          {isEdit ? "Edit Equipment" : "Add Equipment"}
        </Typography>
        <IconButton onClick={handleDialogClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Error message display */}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              label="Serial No."
              placeholder="Placeholder"
              value={formData.serialNo}
              onChange={(e) => handleChange("serialNo", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              label="Equipment Name"
              placeholder="Placeholder"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <CustomSelect
                value={formData.brand}
                onChange={(e) =>
                  handleChange("brand", e.target.value as string)
                }
                label="Brand"
              >
                <MenuItem value="">Placeholder</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>
                    {brand}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <CustomSelect
                value={formData.category}
                onChange={(e) =>
                  handleChange("category", e.target.value as string)
                }
                label="Category"
              >
                <MenuItem value="">Placeholder</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <CustomSelect
                value={formData.subCategory}
                onChange={(e) =>
                  handleChange("subCategory", e.target.value as string)
                }
                label="Sub Category"
              >
                <MenuItem value="">Placeholder</MenuItem>
                {subCategories.map((subCategory) => (
                  <MenuItem key={subCategory} value={subCategory}>
                    {subCategory}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextField
              fullWidth
              label="Purchase Date"
              type="date"
              value={formData.purchaseDate}
              onChange={(e) => handleChange("purchaseDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth>
              <CustomSelect
                value={formData.status}
                onChange={(e) =>
                  handleChange("status", e.target.value as string)
                }
                label="Status"
              >
                <MenuItem value="">Placeholder</MenuItem>
                {statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleDialogClose}
          sx={{
            backgroundColor: colors.primary[800],
            borderRadius: 2,
            color: colors.primary[100],
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: 2,
          }}
          disabled={loading}
        >
          {loading ? "Saving..." : isEdit ? "Update Equipment" : "Add Equipment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEquipmentDialog;

